import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { themeVar as colors, type as typeTokens } from "../design-tokens";
import { getMapBrowserKey, getMapStatus } from "./api";
import type { MapMarker } from "./types";
import { blockStyle } from "../ui/Block";
import {
  BROWSER_GEOCODE_SOURCE, MAP_FIT_PADDING_PX, MAP_MAX_ZOOM, NOT_MAPPED_MESSAGE, frameBounds, geocodeMatchPasses, mapCanvasStyle, type LatLng,
} from "./compMapModel";

/**
 * The location map on the review screen (2026-07-31, owner-approved for BOTH surfaces).
 *
 * WHY THIS COMPONENT HOLDS A KEY AND `StreetViewCell` DOES NOT.
 * Street View is proxied — the server fetches the bytes and the GCP key never reaches the browser.
 * An interactive map cannot work that way: Maps JavaScript runs in this page and authenticates
 * from this page, so its key is necessarily public. No proxy changes that.
 *
 * So the key served to this component is a SECOND, separate, HTTP-referrer-restricted key
 * (`VALUBRIEF_MAPS_BROWSER_KEY`) that opens nothing else. `providers/maps.py::browser_key` refuses
 * to fall back to the server-side key precisely so that this component can never publish the
 * server's Street View / Places / Static Maps surface along with the map. If you are debugging a
 * blank map, the fix is to set the browser key — never to point this at the server key.
 *
 * WHY EVERY PIN IS LABELLED AND LISTED.
 * A map pin reads as authoritative and carries no visual cue when it is wrong. Measured against
 * the live provider on 2026-07-30, four of eight bare street lines from a Plano MLS export
 * resolved to a different state. The server applies the resolvability gate and simply does not
 * send a marker it cannot pin, so this component never has to decide — but it DOES render the
 * legend, because a pin that says "3" and nothing else is not traceable to anything. The legend is
 * what lets the agent match a pin to a row in the comparable table.
 *
 * Renders NOTHING when the provider is off, the key is unset, or there are no markers — never a
 * grey box, a spinner that does not resolve, or an empty map centred on the ocean.
 */

const KIND_SWATCH: Record<string, string> = {
  subject: "#183A6B",
  comp: "#2E6FB8",
  competing: "#B6862C",
};

export function LocationMap({
  markers,
  unmapped = [],
}: {
  markers: MapMarker[];
  /**
   * ADDRESSES THAT COULD NOT BE PINNED, NAMED. Owner-facing consequence of `buildPins`'s
   * pin-or-don't rule (`addressPin.ts`): an address this client cannot place confidently is not
   * plotted at a guess, because a pin in the wrong place is worse than no pin. Until 2026-08-10
   * `CmaAmbFlow` took `.pins` and discarded the rest, so those homes vanished with no signal — an
   * agent looking at three pins for five selected comps had no way to know two were missing, and
   * the map read as a complete picture of where the value came from when it was not.
   *
   * Absence has to be visible or it is a claim. Defaults to `[]` so every existing caller and test
   * renders exactly as before.
   */
  unmapped?: string[];
}) {
  const [key, setKey] = useState<string | null>(null);
  const [attribution, setAttribution] = useState("");
  const [failed, setFailed] = useState(false);
  /** Addresses the browser geocoder could not place under the house-number/ZIP gate. */
  const [geoMissed, setGeoMissed] = useState<string[]>([]);
  /** Addresses the browser geocoder DID place (no stored point): their source line reads "Google geocode". */
  const [geoPlaced, setGeoPlaced] = useState<string[]>([]);
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!markers.length) return; // nothing to plot — never spend a status call or a map load
    let cancelled = false;
    getMapStatus()
      .then((s) => {
        if (cancelled) return;
        if (!s.enabled || !s.browser_key_configured) {
          // Honest inert state, and a DIFFERENT one from a failure: nothing renders, and no
          // request for a key we already know is not there.
          setKey(null);
          return;
        }
        setAttribution(s.attribution);
        return getMapBrowserKey().then((k) => {
          if (!cancelled) setKey(k.key);
        });
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [markers.length]);

  useEffect(() => {
    if (!key || !hostRef.current || !markers.length) return;
    let cancelled = false;
    setGeoMissed([]);
    setGeoPlaced([]);

    // The Maps JavaScript loader is a <script> tag by design — there is no npm-only path that
    // avoids it. Loaded ONCE and reused: a second <script> for the same library logs a console
    // warning and can re-initialise the map underneath us.
    const ensureLoaded = (): Promise<void> =>
      new Promise((resolve, reject) => {
        const w = window as unknown as { google?: { maps?: unknown } };
        if (w.google?.maps) return resolve();
        const existing = document.getElementById("vb-gmaps-loader");
        if (existing) {
          existing.addEventListener("load", () => resolve());
          existing.addEventListener("error", () => reject(new Error("maps failed to load")));
          return;
        }
        const s = document.createElement("script");
        s.id = "vb-gmaps-loader";
        s.async = true;
        s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(key)}`;
        s.onload = () => resolve();
        s.onerror = () => reject(new Error("maps failed to load"));
        document.head.appendChild(s);
      });

    ensureLoaded()
      .then(() => {
        if (cancelled || !hostRef.current) return;
        const g = (window as unknown as { google: any }).google;
        const map = new g.maps.Map(hostRef.current, { mapTypeControl: false, streetViewControl: false });
        const geocoder = new g.maps.Geocoder();
        const placed: LatLng[] = [];
        // Geocoded one at a time, in the browser, against the referrer-restricted key. A marker
        // whose address does not resolve is simply not placed — the same rule the server applies,
        // held again here because the browser geocoder is a different service and can disagree.
        // B2 (2026-11-02): A STORED COORDINATE IS PLACED AS STORED. It is the point the comps map
        // pinned on step 3 (`compMapModel.storedLocation`); geocoding the address again here is how
        // the two maps came to disagree. Only a marker with no stored point is geocoded, and then
        // only a match carrying the same house number and ZIP is placed (`geocodeMatchPasses`).
        const put = (m: MapMarker, lat: number, lng: number) => {
          new g.maps.Marker({
            map,
            position: { lat, lng },
            label: { text: m.label, color: "#ffffff", fontSize: "11px", fontWeight: "600" },
            title: `${m.label} — ${m.address}`,
          });
          placed.push({ lat, lng });
            // Fit to whatever actually resolved. Choosing a centre ourselves would be choosing
            // which properties fall off the edge. NEVER STREET LEVEL (owner, 2026-10-22: one S pin
            // at street zoom): `frameBounds` never frames less than a 0.5 mi circle, and the zoom
            // is held at a neighborhood, so a lone pin still shows where it is.
          const b = frameBounds({ subject: null, radiusMi: 0, pins: placed });
          if (!b) return;
          map.fitBounds(b, MAP_FIT_PADDING_PX);
          g.maps.event?.addListenerOnce?.(map, "idle", () => {
            const z = map.getZoom?.();
            if (typeof z === "number" && z > MAP_MAX_ZOOM) map.setZoom(MAP_MAX_ZOOM);
          });
        };
        markers.forEach((m) => {
          if (typeof m.lat === "number" && typeof m.lon === "number") {
            put(m, m.lat, m.lon);
            return;
          }
          geocoder.geocode({ address: m.address }, (res: any, statusText: string) => {
            if (cancelled) return;
            if (statusText !== "OK" || !res?.[0] || !geocodeMatchPasses(m.address, res[0])) {
              setGeoMissed((prev) => (prev.includes(m.address) ? prev : [...prev, m.address]));
              return;
            }
            const pos = res[0].geometry.location;
            setGeoPlaced((prev) => (prev.includes(m.address) ? prev : [...prev, m.address]));
            put(m, pos.lat(), pos.lng());
          });
        });
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [key, markers]);

  // SQUARE, NOT A STRIP (2026-10-22): the same shape as the comps step's map, through `setProperty` (I-8).
  useLayoutEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    const write = () => {
      for (const [k, v] of Object.entries(mapCanvasStyle(window.innerWidth))) el.style.setProperty(k, v);
    };
    write();
    window.addEventListener("resize", write);
    return () => window.removeEventListener("resize", write);
  }, [key, failed, markers.length]);

  // The unmapped list is worth showing even when the map itself is not: "we could not place these"
  // is true whether or not a tile ever loaded, and it is the half an agent can act on.
  const notPlaced = [...unmapped, ...geoMissed.filter((a) => !unmapped.includes(a))];
  const unmappedNote = notPlaced.length ? (
    <div style={{ marginTop: 12, fontSize: 12, color: colors.textSecondary, lineHeight: 1.5 }} data-testid="map-not-mapped">
      <p style={{ ...blockStyle("copy"), margin: 0 }}>
        {notPlaced.length} not on the map. Each is still listed here, and none is placed at a guess.
      </p>
      <ul style={{ margin: "4px 0 0 0", paddingLeft: 18 }}>
        {notPlaced.map((a) => (<li key={a}>{a} — {NOT_MAPPED_MESSAGE}</li>))}
      </ul>
    </div>
  ) : null;

  /* WHEN THE NOTE IS ALL THERE IS, IT STILL NEEDS AN EDGE (2026-09-01, W3-UIFIX2).
     From `w2-uifix/after/step4-review@1920.png`: with no maps key the whole component collapsed to
     a bare `<p>`, which landed between two bordered summary cards as a paragraph floating on the
     page ground — the one thing on step 4 that belonged to nothing. It is a disclosure about the
     comparable set ("these are still in the valuation, we just could not place them"), which is
     exactly the kind of statement this product must not let read as an aside. Same chrome as the
     map card it stands in for, and a heading that says what it is. */
  if (!markers.length || (!key && !failed)) {
    return unmapped.length ? (
      <section
        aria-labelledby="vb-unmapped-heading"
        style={{ marginTop: 24, border: `1px solid ${colors.border}`, borderRadius: 10, padding: 18, background: colors.bgSurface }}
      >
        <h2 id="vb-unmapped-heading" style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>
          Not shown on the map
        </h2>
        {unmappedNote}
      </section>
    ) : null;
  }

  if (failed) {
    return (
      <p style={{ fontSize: 12, color: colors.textSecondary, marginTop: 12 }}>
        The location map didn&apos;t load. Everything else on this page is unaffected — the map is
        reference context, not part of the valuation.
        {unmapped.length ? ` Separately, we could not place: ${unmapped.join("; ")} (${NOT_MAPPED_MESSAGE}).` : ""}
      </p>
    );
  }

  return (
    <section
      aria-labelledby="vb-map-heading"
      style={{ marginTop: 24, border: `1px solid ${colors.border}`, borderRadius: 10, padding: 18, background: colors.bgSurface }}
    >
      <h2 id="vb-map-heading" style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>
        Where these homes are
      </h2>
      <p style={{ marginTop: 6, marginBottom: 0, fontSize: 13, color: colors.textSecondary, lineHeight: 1.5 }}>
        Your home is marked S. Every numbered pin is listed underneath with its full address, so each
        one can be matched back to the tables on this page.
      </p>

      <div
        ref={hostRef}
        role="img"
        aria-label="Map showing the subject property, the closed comparable sales, and the homes currently on the market"
        style={{ marginTop: 12, borderRadius: 8, background: "#EEF1F5" }}
      />

      <ul style={{ listStyle: "none", padding: 0, margin: "12px 0 0 0", fontSize: 13 }}>
        {markers.map((m) => (
          <li key={`${m.label}-${m.address}`} style={{ display: "flex", gap: 8, alignItems: "baseline", padding: "3px 0" }}>
            <span
              aria-hidden="true"
              style={{
                display: "inline-flex", alignItems: "center", justifyContent: "center",
                minWidth: 20, height: 20, borderRadius: 999, fontSize: 11, fontWeight: 700,
                color: "#fff", background: KIND_SWATCH[m.kind] ?? colors.textSecondary,
              }}
            >
              {m.label}
            </span>
            <span>{m.address}</span>
            {/* Colour carries the tier, text carries the specifics — the swatch is never the only
                thing telling a reader which kind of property this is. */}
            <span style={{ color: colors.textSecondary }}>— {m.kind_label}</span>
            {(m.coord_source || geoPlaced.includes(m.address)) && (
              <span className="vb-map-coord-src" data-testid="map-coord-source">
                · location: {m.coord_source ?? BROWSER_GEOCODE_SOURCE}
              </span>
            )}
          </li>
        ))}
      </ul>

      <p style={{ fontSize: 10, color: colors.textSecondary, margin: "10px 0 0 0", fontFamily: typeTokens.fontUi }}>
        {attribution} — reference context. {markers.some((m) => m.coord_source)
          ? "Pins use the location stated in each sale's source (named above); any other pin is geocoded from its address."
          : "Pin positions are geocoded from each address."} They are not a survey and do not affect
        the valuation.
      </p>
      {unmappedNote}
    </section>
  );
}
